import React from 'react';
import { graphql } from 'gatsby';
import get from 'lodash/get';
import { renderRichText } from 'gatsby-source-contentful/rich-text.js';
import { GatsbyImage as Img, getImage } from 'gatsby-plugin-image';
import HtmlHeader from '../components/HtmlHeader.jsx';
import Layout from '../components/Layout/Layout.jsx';
import SocialMedia from '../components/SocialMedia/SocialMedia.jsx';
import Cta from '../sections/Cta/Cta.jsx';
import RelatedPosts from '../sections/RelatedPosts/RelatedPosts.jsx';
import RichTextRender from '../components/RichTextRender.jsx';

import '../components/Base.scss';
import './BlogPostTemplate.scss';

const HeadingTwo = ({ children }) => <h2 className="heading-five">{children}</h2>;

class BlogPostTemplate extends React.Component {
  render() {

    const currentBlog = get(this.props, 'data.contentfulBlogPost');
    const relatedBlogs = get(this.props, 'data.allContentfulBlogPost.edges');

    const {
      metaTitle,
      metaDescription,
      topic,
      heading,
      author,
      displayDate,
      socialMedia,
      introCopy,
      content,
      structuredData,
      slug,
    } = currentBlog;

    const heroImage = getImage(get(currentBlog, 'heroImage'));
    const heroImageUrl = get(currentBlog, 'heroImage.file.url');
    const heroImageTitle = get(currentBlog, 'heroImage.title');
    const sources = get(currentBlog, 'sources.childMarkdownRemark.html');
    const navbar = get(currentBlog, 'navbar', null);
    const footer = get(currentBlog, 'footer', null);

    return (
      <div className='profile'>
        <HtmlHeader
          canonicalLink={`blogs/${slug}`}
          metaTitle={metaTitle}
          metaDescription={metaDescription}
          heading={heading}
          structuredData={structuredData}
          slug={slug}
          ogUrl={`https://www.profilepensions.co.uk/blogs/${slug}`}
          ogTitle={metaTitle}
          ogImage={heroImageUrl}
          ogType="blog"
        />
        <Layout
          displaySubNav
          subNavLink="/blogs"
          subNavLinkText="Back to blog"
          customNavbar={navbar}
          customFooter={footer}
        >
          <div className="blog-page-section">
            <div className="blog-page-section-wrapper">
              <div className="blog-heading">
                <p>{topic && topic.toUpperCase()}</p>
                <h1>{heading}</h1>
              </div>
              <div className="blog-author">
                <p className="body-two">{`${author}${displayDate ? ` | ${displayDate}` : ''}`}</p>
              </div>
              <div className="blog-social">
                <p>SHARE</p>
                <SocialMedia iconsToDisplay={socialMedia} location={this.props.location.pathname} linkType="share" />
              </div>
              <Img className="blog-hero-image" alt={heroImageTitle} image={heroImage} />
              <div className="blog-intro">
                {renderRichText(introCopy, RichTextRender({ HeadingTwo }))}
              </div>
              <div className="blog-content">
                {renderRichText(content, RichTextRender({ HeadingTwo }))}
              </div>
              <div className="blog-sources" dangerouslySetInnerHTML={{ __html: sources }} />
            </div>
          </div>
          <Cta ctaButtonCopy="Sign up now" />
          {
            relatedBlogs && relatedBlogs.length > 0 && <RelatedPosts related={relatedBlogs} />
          }
        </Layout>
      </div>
    );
  }
}

export default BlogPostTemplate;

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!, $relatedPosts: [String]) {
    contentfulBlogPost(slug: { eq: $slug }) {
      metaTitle
      metaDescription
      topic
      heading
      author
      displayDate
      socialMedia
      slug
      introCopy {
        raw
      }
      content {
        raw
        references {
          ... on ContentfulAsset {
            ... ContentfulAssetFragment
          }
        }
      }
      heroImage {
        gatsbyImageData(width: 700, layout: FULL_WIDTH, placeholder: BLURRED)
        file {
          url
        }
        title
      }
      sources {
        childMarkdownRemark {
          html
        }
      }
      structuredData {
        breadcrumb
        _context
        _type
        mainEntity {
          _type
          abstract
          inLanguage
          url
        }
        creator {
          _type
          name
          sameAs
          telephone
          address {
            _type
            addressCountry
            addressLocality
            postalCode
            streetAddress
          }
        }
      }
      navbar {
        topLevelLinks {
          ... on ContentfulLink {
            __typename
            name
            displayText
            url
          }
          ... on ContentfulNavbarTopLevelLink {
            __typename
            name
            displayText
            subLinks {
              name
              displayText
              url
            }
          }
        }
        colour
      }
      footer {
        name
        topParagraph {
          raw
        }
        quickLinks {
          displayText
          url
          name
        }
        contactUs {
          raw
        }
        bottomParagraph {
          raw
        }
        colour
      }
    }
    allContentfulBlogPost(filter: { slug: { in: $relatedPosts, ne: $slug } }, sort: {date: DESC}) {
      edges {
        node {
          heading
          slug
          topic
          heroImage {
            gatsbyImageData(width: 700, layout: FULL_WIDTH, placeholder: BLURRED)
          }
        }
      }
    }
  }
`;
